import React, { useContext } from "react";
import axios from "axios";
import { base_url } from "../../config";
import RegistrationContext from "../../contexts/RegistrationContext";

import { useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Seat #1 is the delegate — their conference badge is their entrance. The seats after it
 * are the ones they bought for guests, and those are the ones that get a printed ticket.
 */
const seatLabel = (index: number) => (index === 0 ? "You" : `Guest #${index}`);

export function Attendance({ prefSaved }: { prefSaved?: boolean }) {
  const { data, setData } = useContext(RegistrationContext);
  const [banquet, setBanquet] = useState<string[]>([...data.gala_dinners]);
  const [reception, setReception] = useState<string[]>([...data.receptions]);
  const [studentReception, setStudentReception] = useState<string[]>([
    ...data.student_receptions,
  ]);

  const [preferencesSaved, setPreferencesSaved] = useState(prefSaved || false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const ans = await axios.post(`${base_url}/api/attendance`, {
        reception,
        banquet,
        studentReception,
      });
      setPreferencesSaved(true);
      setData(ans.data.data);
    } catch (error) {
      console.error("Error submitting attendance:", error);
    }
  };

  if (preferencesSaved) {
    return (
      <div className="rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-bold">Preferences Saved</h2>
        <p className="text-gray-700">
          Your attendance preferences have been successfully saved.
        </p>
        <button
          className="mt-6 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          onClick={() => setPreferencesSaved(false)}
        >
          OK
        </button>
      </div>
    );
  }

  const hasSocialEvents =
    data.receptions.length > 0 ||
    data.student_receptions.length > 0 ||
    data.gala_dinners.length > 0;

  return (
    <div
      className="rounded-lg border bg-white p-6 shadow-sm overflow-y-scroll"
      id="attendance"
    >
      <h2 className="mb-4 text-xl font-bold">Attendance & Choices</h2>
      {data.status === "pending" && (
        <div className="mb-6 rounded-lg bg-red-100 border border-red-400 text-red-800 px-4 py-3">
          Your registration has items marked as undecided or empty choices. <br />
          Please review and complete your selections.
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-8">
        {hasSocialEvents && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <label className="text-base font-semibold text-gray-900">
                Social Events
              </label>
            </div>
            <p className="text-sm text-gray-600">
              Please confirm your attendance for the following events:
            </p>

            {data.receptions.map((_g: any, index: number) => (
              <div className="space-y-3" key={`reception-${index}`}>
                <label className="text-sm font-medium text-gray-900">
                  Welcome Reception
                  {data.receptions.length > 1 && <> - {seatLabel(index)}</>}
                </label>
                <div className="flex gap-6">
                  {["Yes", "No"].map((v) => (
                    <label
                      key={v}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`reception_${index}`}
                        value={v}
                        checked={reception[index] === v}
                        onChange={(e) =>
                          setReception((prev) => {
                            const u = [...prev];
                            u[index] = e.target.value;
                            return u;
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {v === "Yes" ? "Yes, I will attend" : "No, I will not attend"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {data.student_receptions.map((_g: any, index: number) => (
              <div className="space-y-3" key={`student-reception-${index}`}>
                <label className="text-sm font-medium text-gray-900">
                  Student Reception
                  {data.student_receptions.length > 1 && (
                    <> - {seatLabel(index)}</>
                  )}
                </label>
                <div className="flex gap-6">
                  {["Yes", "No"].map((v) => (
                    <label
                      key={v}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`studentReception_${index}`}
                        value={v}
                        checked={studentReception[index] === v}
                        onChange={(e) =>
                          setStudentReception((prev) => {
                            const u = [...prev];
                            u[index] = e.target.value;
                            return u;
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {v === "Yes" ? "Yes, I will attend" : "No, I will not attend"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {data.gala_dinners.map((_g: any, index: number) => (
              <div className="space-y-3" key={`banquet-${index}`}>
                <label className="text-sm font-medium text-gray-900">
                  Conference Banquet
                  {data.gala_dinners.length > 1 && <> - {seatLabel(index)}</>}
                </label>
                <div className="flex gap-6">
                  {[
                    { v: "Yes", label: "Yes, I will attend" },
                    { v: "No", label: "No, I will not attend" },
                  ].map((o) => (
                    <label
                      key={o.v}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`banquet_${index}`}
                        value={o.v}
                        checked={banquet[index] === o.v}
                        onChange={(e) =>
                          setBanquet((prev) => {
                            const u = [...prev];
                            u[index] = e.target.value;
                            return u;
                          })
                        }
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{o.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pt-6">
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Registration Details
          </button>
        </div>
      </form>
    </div>
  );
}
